import { useState } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Loader2, FileText, Trash2, Edit2, Check, X } from 'lucide-react';
import { toast } from 'sonner';

interface ParsedPrompt {
  number: number;
  title: string;
  prompt: string;
}

interface BulkPromptJobCreatorProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  availableModels: { id: string; model_key: string; display_name: string }[];
  onCreateJobs: (prompts: ParsedPrompt[], settings: JobSettings) => Promise<void>;
}

interface JobSettings {
  models_to_use: string[];
  size: string;
  quality: string;
  style: string;
  images_per_prompt: number;
}

const IMAGE_SIZES = [
  { value: '1024x1024', label: '1024x1024 (Square)' },
  { value: '1792x1024', label: '1792x1024 (Landscape)' },
  { value: '1024x1792', label: '1024x1792 (Portrait)' }
];

export const BulkPromptJobCreator = ({ 
  open, 
  onOpenChange, 
  availableModels,
  onCreateJobs 
}: BulkPromptJobCreatorProps) => {
  const [rawContent, setRawContent] = useState('');
  const [parsedPrompts, setParsedPrompts] = useState<ParsedPrompt[]>([]);
  const [selectedModels, setSelectedModels] = useState<string[]>([]);
  const [size, setSize] = useState('1024x1024');
  const [quality, setQuality] = useState('standard');
  const [style, setStyle] = useState('vivid');
  const [imagesPerPrompt, setImagesPerPrompt] = useState('10');
  const [isCreating, setIsCreating] = useState(false);
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [editTitle, setEditTitle] = useState('');
  const [editPrompt, setEditPrompt] = useState('');

  // Parse multi-prompt content
  const parseContent = () => {
    if (!rawContent.trim()) {
      toast.error('Please paste content to parse');
      return;
    }

    const matches: ParsedPrompt[] = [];
    
    // Split by numbered items (1., 2., etc.)
    const sections = rawContent.split(/(?=\d+\.)/);
    
    for (const section of sections) {
      if (!section.trim()) continue;
      
      // Match: number. Title (with optional newlines) "Prompt text"
      // Support both straight quotes "", curly quotes "", and single quotes ''
      const numberMatch = section.match(/^(\d+)\.\s*/);
      if (!numberMatch) continue;
      
      const number = parseInt(numberMatch[1]);
      const rest = section.slice(numberMatch[0].length);
      
      // Find the quoted prompt - support various quote styles
      const quoteMatch = rest.match(/[""\u201C\u201D''\u2018\u2019]([^""\u201C\u201D''\u2018\u2019]+)[""\u201C\u201D''\u2018\u2019]/);
      if (!quoteMatch) continue;
      
      const prompt = quoteMatch[1].trim();
      
      // Title is everything before the quote
      const quoteStart = rest.indexOf(quoteMatch[0]);
      const title = rest.slice(0, quoteStart).trim().replace(/[\n\r]+/g, ' ').replace(/\s*[–-]\s*$/, '').trim();
      
      if (title && prompt) {
        matches.push({ number, title, prompt });
      }
    }

    if (matches.length === 0) {
      toast.error('Could not parse prompts. Check format: "1. Title\\n\\"Prompt text...\\"');
      return;
    }

    setParsedPrompts(matches);
    toast.success(`Parsed ${matches.length} prompts`);
  };

  const removePrompt = (index: number) => {
    setParsedPrompts(prev => prev.filter((_, i) => i !== index));
  };

  const startEditing = (index: number) => {
    setEditingIndex(index);
    setEditTitle(parsedPrompts[index].title);
    setEditPrompt(parsedPrompts[index].prompt);
  };

  const saveEdit = () => {
    if (editingIndex === null) return;
    setParsedPrompts(prev => prev.map((p, i) => 
      i === editingIndex ? { ...p, title: editTitle, prompt: editPrompt } : p
    ));
    setEditingIndex(null);
  };

  const cancelEdit = () => {
    setEditingIndex(null);
  };

  const handleCreateAll = async () => {
    if (parsedPrompts.length === 0) {
      toast.error('No prompts to create jobs from');
      return;
    }
    if (selectedModels.length === 0) {
      toast.error('Please select at least one model');
      return;
    }
    const images = parseInt(imagesPerPrompt);
    if (isNaN(images) || images < 1) {
      toast.error('Please enter a valid number of images per prompt');
      return;
    }

    setIsCreating(true);
    try {
      await onCreateJobs(parsedPrompts, {
        models_to_use: selectedModels,
        size,
        quality,
        style,
        images_per_prompt: images
      });
      
      // Reset state on success
      setRawContent('');
      setParsedPrompts([]);
      onOpenChange(false);
    } catch (error) {
      console.error('Error creating bulk jobs:', error);
    } finally {
      setIsCreating(false);
    }
  };

  const totalImages = parsedPrompts.length * parseInt(imagesPerPrompt || '0');

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh] bg-background/95 backdrop-blur-md border-border/50">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5" />
            Bulk Multi-Prompt Job Creator
          </DialogTitle>
          <DialogDescription>
            Paste multiple prompts to create separate automation jobs for each
          </DialogDescription>
        </DialogHeader>

        <div className="grid gap-4 py-4">
          {/* Raw Content Input */}
          {parsedPrompts.length === 0 && (
            <div className="space-y-2">
              <Label>Paste Multi-Prompt Content</Label>
              <Textarea
                placeholder={`1. Merry Christmas – simple bold lettering

"Hand-lettered Merry Christmas typography, bold festive font..."

2. Merry Christmas – cute rounded font

"Cute rounded Merry Christmas lettering in playful pastel colors..."`}
                value={rawContent}
                onChange={(e) => setRawContent(e.target.value)}
                rows={10}
                className="font-mono text-sm"
              />
              <div className="flex justify-end">
                <Button onClick={parseContent} disabled={!rawContent.trim()}>
                  Parse Prompts
                </Button>
              </div>
            </div>
          )}

          {/* Parsed Prompts Preview */}
          {parsedPrompts.length > 0 && (
            <>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Badge variant="secondary">{parsedPrompts.length} prompts detected</Badge>
                  <span className="text-sm text-muted-foreground">
                    = {totalImages} total images
                  </span>
                </div>
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => setParsedPrompts([])}
                >
                  Clear & Re-paste
                </Button>
              </div>

              <ScrollArea className="h-[200px] rounded-md border p-2">
                <div className="space-y-2">
                  {parsedPrompts.map((prompt, index) => (
                    <div 
                      key={index} 
                      className="p-3 rounded-lg bg-muted/50 border border-border/50"
                    >
                      {editingIndex === index ? (
                        <div className="space-y-2">
                          <Input
                            value={editTitle}
                            onChange={(e) => setEditTitle(e.target.value)}
                            placeholder="Job title"
                            className="text-sm"
                          />
                          <Textarea
                            value={editPrompt}
                            onChange={(e) => setEditPrompt(e.target.value)}
                            placeholder="Prompt"
                            rows={2}
                            className="text-sm"
                          />
                          <div className="flex gap-1">
                            <Button size="sm" variant="ghost" onClick={saveEdit}>
                              <Check className="h-4 w-4" />
                            </Button>
                            <Button size="sm" variant="ghost" onClick={cancelEdit}>
                              <X className="h-4 w-4" />
                            </Button>
                          </div>
                        </div>
                      ) : (
                        <div className="flex items-start justify-between gap-2">
                          <div className="flex-1 min-w-0">
                            <div className="font-medium text-sm">
                              {prompt.number}. {prompt.title}
                            </div>
                            <div className="text-xs text-muted-foreground line-clamp-2 mt-1">
                              {prompt.prompt}
                            </div>
                          </div>
                          <div className="flex gap-1 shrink-0">
                            <Button 
                              size="sm" 
                              variant="ghost"
                              onClick={() => startEditing(index)}
                            >
                              <Edit2 className="h-3 w-3" />
                            </Button>
                            <Button 
                              size="sm" 
                              variant="ghost"
                              onClick={() => removePrompt(index)}
                            >
                              <Trash2 className="h-3 w-3" />
                            </Button>
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </ScrollArea>

              {/* Settings */}
              <div className="grid gap-4 md:grid-cols-4">
                <div className="space-y-2">
                  <Label>Images Per Prompt</Label>
                  <Input
                    type="number"
                    min="1"
                    max="100"
                    value={imagesPerPrompt}
                    onChange={(e) => setImagesPerPrompt(e.target.value)}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Size</Label>
                  <Select value={size} onValueChange={setSize}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {IMAGE_SIZES.map((s) => (
                        <SelectItem key={s.value} value={s.value}>
                          {s.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Quality</Label>
                  <Select value={quality} onValueChange={setQuality}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="standard">Standard</SelectItem>
                      <SelectItem value="hd">HD</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Style</Label>
                  <Select value={style} onValueChange={setStyle}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="vivid">Vivid</SelectItem>
                      <SelectItem value="natural">Natural</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Model Selection */}
              <div className="space-y-2">
                <Label>Models (select multiple)</Label>
                <div className="grid gap-2 md:grid-cols-3">
                  {availableModels.map((model) => (
                    <label
                      key={model.id}
                      className="flex items-center space-x-2 cursor-pointer rounded-md border p-2 hover:bg-accent"
                    >
                      <input
                        type="checkbox"
                        checked={selectedModels.includes(model.model_key)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setSelectedModels([...selectedModels, model.model_key]);
                          } else {
                            setSelectedModels(selectedModels.filter(m => m !== model.model_key));
                          }
                        }}
                        className="h-4 w-4"
                      />
                      <span className="text-sm">{model.display_name}</span>
                    </label>
                  ))}
                </div>
              </div>
            </>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          {parsedPrompts.length > 0 && (
            <Button 
              onClick={handleCreateAll} 
              disabled={isCreating || selectedModels.length === 0}
              className="bg-gradient-to-r from-primary to-primary/80"
            >
              {isCreating ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Creating {parsedPrompts.length} Jobs...
                </>
              ) : (
                `Create ${parsedPrompts.length} Jobs (${totalImages} images)`
              )}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
